import React from 'react';
import { MapPin, Search, X, PenLine } from 'lucide-react';
import { MAJOR_CITIES } from '../../data/budget-countries';

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  countryCode: string;
  placeholder?: string;
  className?: string;
}

export function CityAutocomplete({
  value,
  onChange,
  countryCode,
  placeholder = 'Rechercher une ville...',
  className = '',
}: CityAutocompleteProps) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [isCustomMode, setIsCustomMode] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(value);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  React.useEffect(() => {
    setInputValue('');
    setIsCustomMode(false);
    onChange('');
  }, [countryCode]);

  const cities = React.useMemo(() => {
    return MAJOR_CITIES[countryCode] || [];
  }, [countryCode]);

  const filtered = React.useMemo(() => {
    if (!inputValue.trim()) return cities;
    const query = inputValue.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return cities.filter(city => {
      const normalized = city.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return normalized.includes(query);
    });
  }, [cities, inputValue]);

  const showDropdown = isFocused && countryCode && !isCustomMode;
  const exactMatch = cities.some(c => c.toLowerCase() === inputValue.trim().toLowerCase());

  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (city: string) => {
    setInputValue(city);
    onChange(city);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleCustomMode = () => {
    setIsCustomMode(true);
    setInputValue('');
    onChange('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    setIsCustomMode(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          placeholder={!countryCode ? 'Choisis un pays d\'abord' : isCustomMode ? 'Tape le nom de ta ville...' : placeholder}
          disabled={!countryCode}
          className={`w-full pl-10 pr-9 py-3.5 sm:py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-base sm:text-sm dark:bg-gray-700/50 dark:text-white focus:outline-none focus:border-teal-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        )}
      </div>

      {isCustomMode && isFocused && !inputValue.trim() && (
        <div className="absolute z-50 left-0 right-0 mt-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-3">
          <button
            type="button"
            onClick={() => { setIsCustomMode(false); }}
            className="w-full flex items-center gap-2.5 py-1.5 text-left text-sm text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
          >
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Revenir a la liste des villes</span>
          </button>
        </div>
      )}

      {showDropdown && (
        <div className="absolute z-50 left-0 right-0 mt-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-52 overflow-y-auto overscroll-contain">
          {filtered.length > 0 ? (
            <>
              {filtered.map((city) => {
                const isSelected = city.toLowerCase() === inputValue.trim().toLowerCase();
                return (
                  <button
                    key={city}
                    type="button"
                    onClick={() => handleSelect(city)}
                    className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-sm transition-colors hover:bg-teal-50 dark:hover:bg-teal-900/20 ${
                      isSelected
                        ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <MapPin className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-teal-500' : 'text-gray-400'}`} />
                    <span>{city}</span>
                  </button>
                );
              })}
              {inputValue.trim() && !exactMatch && (
                <div className="px-3.5 py-2 border-t border-gray-100 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => handleSelect(inputValue.trim())}
                    className="w-full flex items-center gap-2.5 py-1 text-left text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                  >
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Utiliser "<strong>{inputValue.trim()}</strong>"</span>
                  </button>
                </div>
              )}
              <div className="px-3.5 py-2.5 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleCustomMode}
                  className="w-full flex items-center gap-2.5 py-1 text-left text-sm text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                >
                  <PenLine className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Autre : saisir manuellement</span>
                </button>
              </div>
            </>
          ) : (
            <div className="px-3.5 py-3">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1.5">Aucune ville trouvee</p>
              {inputValue.trim() && (
                <button
                  type="button"
                  onClick={() => handleSelect(inputValue.trim())}
                  className="w-full flex items-center gap-2.5 py-1 text-left text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Utiliser "<strong>{inputValue.trim()}</strong>"</span>
                </button>
              )}
              <button
                type="button"
                onClick={handleCustomMode}
                className="w-full flex items-center gap-2.5 py-1 mt-1 text-left text-sm text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
              >
                <PenLine className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Saisir manuellement</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
